/* eslint-disable react/prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { Row, Column } from 'hedron';
import { NBSPAfterSingleLetters, safeHtmlWithBr } from 'common/methods/string';
import { Calculator as CalculatorModel } from 'models';
import ds, { media } from 'designsystem';
import CalculatorInputRow from 'kentico-blocks/Calculator/CalculatorInputRow';
import {
  ButtonTextWhite,
  Heading,
  ParagraphWhite,
  ParagraphWhiteBig,
  Price,
  CalculatorContainer,
  Currency,
  WhiteHeadline,
  WhiteHorizontalLine,
  WhiteArrow,
} from 'kentico-blocks/Calculator/styled';
import styled from 'styled-components';

const SideInfo = styled(Column)`
  position: relative;
  height: 100%;
  background-color: ${ds.get('colors.brand.product')};
  margin-top: ${ds.get('metrics.l')};
  padding-top: ${ds.get('metrics.m')};
  padding-bottom: ${ds.get('metrics.m')};

  ${media.desktop`
    margin-top: 0;
    color: ${ds.get('colors.brand.white')};
    text-align: start;
    padding-left: 8%;
    padding-top: ${ds.get('metrics.xxl')};
    padding-bottom: ${ds.get('metrics.xxl')};
  `};
`;

const SideInfoBackground = styled.div`
  display: block;
  position: absolute;
  background-color: ${ds.get('colors.brand.product')};
  width: 100vw;
  height: 140%;
  left: 0;
  top: 0;

  ${media.desktop`
    height: 100%;
  `}
`;

const SideInfoContent = styled.aside`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  margin-top: 0;

  ${media.desktop`
    margin-top: ${ds.get('metrics.xxl')};
  `}
`;

const InputColumn = styled(Column)`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
  margin-top: ${ds.get('metrics.s')};

  ${media.desktop`
    margin-top: ${ds.get('metrics.xl')};
  `}
`;

const MiddleText = styled.div`
  padding-top: 0;
  padding-bottom: ${ds.get('metrics.m')};

  ${media.desktop`
    padding-top: ${ds.get('metrics.s')};
    padding-bottom: 0;
  `}
`;

const StyledRow = styled(Row)`
  margin-top: ${ds.get('metrics.s')};
  height: 100%;

  ${media.tablet`
    margin-top: ${ds.get('metrics.l')};
  `}

  ${media.desktop`
    margin-top: 0;
  `}
`;

const AsideWhiteText = styled(Column)`
  color: ${ds.brand('white')};
  font-family: ${ds.get('type.fontFamily.text')};
  line-height: ${ds.get('metrics.s')};
  font-size: ${ds.fs('s')};
  font-weight: ${ds.get('type.fontWeight.light')};
  text-align: start;
  margin-bottom: 0;
  margin-top: ${ds.get('metrics.m')};
  padding-left: 0;
  padding-right: 0;

  ${media.mobile`
    margin-top: 0;
 `}
`;

const WhiteRightArrow = styled(WhiteArrow)`
  transform: translate(2px, 2px) rotate(-45deg);
`;

@inject('calculatorStore')
@observer
export default class CalculatorMainView extends Component {
  static propTypes = {
    data: PropTypes.instanceOf(CalculatorModel).isRequired,
    sideInfoRef: PropTypes.func,
  };

  render() {
    const {
      onClickNext,
      calculatorStore,
      sideInfoRef,
      data: {
        currency,
        mainHeading,
        detailsViewLinkText,
        sideInfoImplementationText,
        sideInfoOptimisationFrequency,
        sideInfoOptimisationPeriod,
        sideInfoReimburseText,
        sideInfoText,
        conferenceRoomsMainViewIcon,
        employeesMainViewIcon,
        desksMainViewIcon,
        parkingSpotsMainViewIcon,
        numberOfConferenceRoomsLabel,
        monthlyM2RentPriceLabel,
        numberOfEmployeesLabel,
        averageEmployeeSalaryPerHourLabel,
        numberOfDesksLabel,
        numberOfParkingSpotsLabel,
        pricePerParkingSpotLabel,
        asideNote,
      },
    } = this.props;

    return (
      <React.Fragment>
        <CalculatorContainer>
          <StyledRow>
            <InputColumn
              lg={7}
            >
              <Heading>
                {mainHeading}
              </Heading>
              <CalculatorInputRow
                icon={conferenceRoomsMainViewIcon}
                firstInputLabel={numberOfConferenceRoomsLabel}
                secondInputLabel={monthlyM2RentPriceLabel}
                firstValueObservableBox={calculatorStore.numberOfConferenceRooms}
                secondValueObservableBox={calculatorStore.monthlyM2RentPrice}
                currency={currency}
              />
              <CalculatorInputRow
                icon={employeesMainViewIcon}
                firstInputLabel={numberOfEmployeesLabel}
                secondInputLabel={averageEmployeeSalaryPerHourLabel}
                firstValueObservableBox={calculatorStore.numberOfEmployees}
                secondValueObservableBox={calculatorStore.averageEmployeeSalaryPerH}
                currency={currency}
              />
              <CalculatorInputRow
                icon={desksMainViewIcon}
                firstInputLabel={numberOfDesksLabel}
                firstValueObservableBox={calculatorStore.numberOfDesks}
                currency={currency}
              />
              <CalculatorInputRow
                icon={parkingSpotsMainViewIcon}
                firstInputLabel={numberOfParkingSpotsLabel}
                secondInputLabel={pricePerParkingSpotLabel}
                firstValueObservableBox={calculatorStore.numberOfParkingSpots}
                secondValueObservableBox={calculatorStore.pricePerParkingSpot}
                currency={currency}
              />
            </InputColumn>
            <SideInfo
              lg={5}
            >
              <SideInfoBackground />
              <SideInfoContent
                innerRef={(ref) => { sideInfoRef(ref); }}
              >
                <WhiteHeadline>
                  <Price>
                    {calculatorStore.totalOptimisationPerMonth.toLocaleString()}
                  </Price>
                  {' '}
                  <Currency>
                    {`${currency} / ${sideInfoOptimisationFrequency}`}
                  </Currency>
                </WhiteHeadline>
                <ParagraphWhite
                  dangerouslySetInnerHTML={{ __html: safeHtmlWithBr(sideInfoText) }}
                />
                <MiddleText>
                  <ParagraphWhiteBig>
                    {`${sideInfoImplementationText}
                    ${calculatorStore.costOfZoniferoIn5Years.toLocaleString()}
                    ${currency}
                    ${sideInfoReimburseText} `}
                    <Price>
                      {`${calculatorStore.yourReturnOfInvestmentTime} ${sideInfoOptimisationPeriod}`}
                    </Price>
                  </ParagraphWhiteBig>
                </MiddleText>
                <WhiteHorizontalLine />
                <ButtonTextWhite
                  mobileToRight
                  onClick={onClickNext}
                >
                  {detailsViewLinkText}
                  <WhiteRightArrow />
                </ButtonTextWhite>
                <WhiteHorizontalLine />
                <AsideWhiteText
                  md={6}
                  lg={12}
                  dangerouslySetInnerHTML={
                    { __html: NBSPAfterSingleLetters(safeHtmlWithBr(asideNote)) }
                  }
                />
              </SideInfoContent>
            </SideInfo>
          </StyledRow>
        </CalculatorContainer>
      </React.Fragment>
    );
  }
}
CalculatorMainView.defaultProps = {
  sideInfoRef: () => null,
};



// WEBPACK FOOTER //
// ./src/kentico-blocks/Calculator/CalculatorMainView/index.jsx